// CORE-509 — the Путь света celebration, spoken by Agapella inside her pop-up.
//
// Same product decision as the onboarding tour (CORE-496): anything she
// initiates should read as a conversation with her, not as the app talking at
// you. So this renders like her chat — her avatar, her bubble — and the
// detailed CORE-272 screen becomes something she OFFERS rather than something
// that appears on its own.
//
// She is not the source of the facts. The bubble text is a phrasing; the level,
// the medallion, the progress and the rights that actually opened all live on
// the screen behind «Посмотреть», which reads them from the backend contract.
// Nothing here may state a right — see `celebrationCopy`.
//
// Presentation only: the queue, the acknowledgement and the details flag live
// in CelebrationContext.
import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { AGAPELLA_AI_ICON } from '../constants/aiCompanions';
import { useCelebration } from '../components/gamification/CelebrationContext';
import { celebrationCopy } from '../components/gamification/celebrationCopy';
import { useCelebrationLine } from '../components/gamification/useCelebrationLine';
import { useTheme } from '../context/ThemeContext';
import { typographyTokens } from '../styles/typographyTokens';

import { ON_ACCENT_TEXT } from '../styles/accentText';
/** Her message bubble — same shape as an incoming message in the chat thread. */
const Bubble: React.FC<{ text: string }> = ({ text }) => {
  const { colors } = useTheme();
  return (
    <View style={styles.row}>
      <Image source={AGAPELLA_AI_ICON} style={styles.avatar} />
      <View
        style={[
          styles.bubble,
          { backgroundColor: colors.surfaceWarm, borderColor: colors.borderSubtle },
        ]}
      >
        <Text style={[styles.text, { color: colors.textSecondary }]}>{text}</Text>
      </View>
    </View>
  );
};

/**
 * CORE-509 — she is writing her line. The wait is short and capped
 * (`useCelebrationLine`), and this is what makes it legible instead of a blank
 * pop-up: in a chat surface, a pause with three dots is her thinking, while
 * text that rewrites itself a second later is a glitch.
 */
const Typing: React.FC = () => {
  const { colors } = useTheme();
  return (
    <View style={styles.row} testID="agapella-celebration-typing">
      <Image source={AGAPELLA_AI_ICON} style={styles.avatar} />
      <View
        style={[
          styles.typingBubble,
          { backgroundColor: colors.surfaceWarm, borderColor: colors.borderSubtle },
        ]}
        accessibilityLabel="Агапелла печатает"
      >
        {[0, 1, 2].map((i) => (
          <View key={i} style={[styles.dot, { backgroundColor: colors.textMuted }]} />
        ))}
      </View>
    </View>
  );
};

export const AgapellaCelebrationPanel: React.FC = () => {
  const { event, openDetails, dismiss } = useCelebration();
  const { colors } = useTheme();
  // Her wording when it arrives in time, the static copy otherwise. The hook is
  // called unconditionally — hooks may not sit behind the early return below.
  const { text, pending } = useCelebrationLine(event);

  if (!event) return null;

  const copy = celebrationCopy(event);

  return (
    <View style={styles.root} testID="agapella-celebration-panel">
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {pending ? <Typing /> : <Bubble text={text} />}
      </ScrollView>

      <View style={[styles.actions, { borderTopColor: colors.borderSubtle }]}>
        <Pressable
          onPress={openDetails}
          accessibilityRole="button"
          testID="agapella-celebration-details"
          style={[styles.primaryBtn, { backgroundColor: colors.solar }]}
        >
          <Text style={styles.primaryLabel}>{copy.primaryLabel}</Text>
        </Pressable>
        {/* Closing without opening the details is a first-class outcome: the
            decision was explicitly that we don't march anyone into the screen. */}
        <Pressable
          onPress={dismiss}
          accessibilityRole="button"
          testID="agapella-celebration-dismiss"
          style={[styles.secondaryBtn, { borderColor: colors.borderStrong }]}
        >
          <Text style={[styles.secondaryLabel, { color: colors.textMuted }]}>
            {copy.secondaryLabel}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, gap: 12 },
  typingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: 16,
    borderTopLeftRadius: 4,
    borderWidth: 1,
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  dot: { width: 6, height: 6, borderRadius: 3, opacity: 0.6 },
  row: { flexDirection: 'row', alignItems: 'flex-end', gap: 8 },
  avatar: { width: 28, height: 28, borderRadius: 14 },
  bubble: {
    flex: 1,
    borderRadius: 16,
    borderTopLeftRadius: 4,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  text: { ...typographyTokens.body },
  actions: { borderTopWidth: 1, padding: 12, gap: 8 },
  primaryBtn: { borderRadius: 12, paddingVertical: 12, alignItems: 'center' },
  primaryLabel: { ...typographyTokens.buttonPrimary, color: ON_ACCENT_TEXT },
  secondaryBtn: {
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 10,
    alignItems: 'center',
  },
  secondaryLabel: { ...typographyTokens.buttonSecondary },
});
